import SearchIcon from '@mui/icons-material/Search'
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
  Stack,
} from '@mui/material'
import type { FormContextValue } from 'dynamic-uniforms'
import { connectField, FormContext, useFormContext } from 'dynamic-uniforms'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCiboAutoForm } from '../CiboAutoFormContext'
import { FieldLabel } from '../FieldLabel'
import { ValidationMessages } from '../ValidationMessages'
import { PlaceResult, PlaceSuggestion, useGooglePlaces } from './useGooglePlaces'

interface AddressWithTypeaheadProps {
  name: string
  label?: string
  value?: Record<string, string>
  onChange: (value: Record<string, string> | undefined) => void
  field?: any
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  error?: any
  [key: string]: any
}

const ADDRESS_FIELD_MAP: Record<string, keyof PlaceResult> = {
  streetAddress: 'streetAddress',
  street1: 'streetAddress',
  address1: 'streetAddress',
  city: 'city',
  state: 'state',
  postalCode: 'postalCode',
  zipCode: 'postalCode',
  zip: 'postalCode',
}

function AddressWithTypeaheadInner({
  name,
  label,
  value,
  onChange,
  field,
  disabled,
  readOnly,
  required,
  ...props
}: AddressWithTypeaheadProps) {
  const { t } = useTranslation('@cibo/ui/AddressWithTypeahead')
  const ctx = useFormContext()
  const { renderDisplay, touch } = useCiboAutoForm()
  const safeRenderDisplay = typeof renderDisplay === 'function' ? renderDisplay : undefined
  const safeTouch = typeof touch === 'function' ? touch : undefined
  const [modalOpen, setModalOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const apiKey =
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GOOGLE_PLACES_API_KEY) ||
    ''

  const uiParams = field?.['ui-params'] || {}
  const regionCodes = uiParams.countryRestriction ? [].concat(uiParams.countryRestriction) : ['us']

  const properties = field?.properties || {}
  const fieldNames = Object.keys(properties)

  const { suggestions, loading, fetchSuggestions, getPlaceDetails, clearSuggestions } =
    useGooglePlaces({ apiKey, regionCodes })

  const handlePlaceSelected = useCallback(
    async (suggestion: PlaceSuggestion | null) => {
      if (!suggestion) return

      const place: PlaceResult | null = await getPlaceDetails(suggestion.placeId)
      if (!place) return

      const newValue: Record<string, string> = { ...(value || {}) }

      for (const fieldKey of fieldNames) {
        const placeKey = ADDRESS_FIELD_MAP[fieldKey]
        if (placeKey && place[placeKey] !== undefined) {
          newValue[fieldKey] = place[placeKey]
        }
      }

      onChange(newValue)
      safeTouch?.(name)
      clearSuggestions()
      setModalOpen(false)
      setInputValue('')
    },
    [value, onChange, ctx, name, safeTouch, getPlaceDetails, clearSuggestions, fieldNames]
  )

  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
    setInputValue('')
    clearSuggestions()
  }, [clearSuggestions])

  // Provide nested context so child fields resolve relative to this object
  const nestedContext = useMemo<FormContextValue>(
    () => ({
      ...ctx,
      namePrefix: name,
    }),
    [ctx, name]
  )

  return (
    <Box data-testid={`address-typeahead-${name}`}>
      {label && <FieldLabel label={label} required={required} />}

      {/* Find Address button */}
      {!disabled && !readOnly && (
        <Box mb={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SearchIcon />}
            onClick={() => setModalOpen(true)}
            data-testid={`address-lookup-btn-${name}`}
          >
            {t('findAddress')}
          </Button>
        </Box>
      )}

      {/* Address fields rendered via normal form machinery */}
      <FormContext.Provider value={nestedContext}>
        <Stack spacing={1} pl={uiParams.nestIndent ? 2 : 0}>
          {fieldNames.map(fieldName =>
            safeRenderDisplay ? safeRenderDisplay({ field: fieldName, itemProps: undefined }) : null
          )}
        </Stack>
      </FormContext.Provider>

      {/* Async validation warnings (e.g. PO box check) */}
      <ValidationMessages fieldName={name} />

      {/* Address lookup modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
        data-testid={`address-lookup-modal-${name}`}
      >
        <DialogTitle>{t('findAddress')}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Autocomplete
              data-testid={`address-search-${name}`}
              freeSolo
              autoFocus
              options={suggestions}
              getOptionLabel={option => (typeof option === 'string' ? option : option.description)}
              filterOptions={x => x}
              loading={loading}
              inputValue={inputValue}
              onInputChange={(_event, newValue, reason) => {
                setInputValue(newValue)
                if (reason === 'input') {
                  fetchSuggestions(newValue)
                }
              }}
              onChange={(_event, newValue) => {
                if (newValue && typeof newValue !== 'string') {
                  handlePlaceSelected(newValue)
                }
              }}
              renderInput={params => (
                <MuiTextField
                  {...params}
                  autoFocus
                  placeholder={t('searchPlaceholder')}
                  fullWidth
                  data-testid={`address-search-input-${name}`}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t('cancel')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export const AddressWithTypeahead = connectField(AddressWithTypeaheadInner)
export default AddressWithTypeahead
